import { UserRetailerCheckoutOrderStatusV2 } from "@/components/Checkout/types";
import React from "react";
import { Box, BoxProps, styled } from "@mui/material";
import { hexadecimal, MOBILE_SCREEN_SIZE } from "@brandclub/common-ui";

const SvgContainer = styled("svg")(({ theme }) => ({
  width: 20,
  height: 20,
  fill: theme.palette.primary.main,
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    width: 12,
    height: 12,
  },
}));

const BadgeContainer = styled(Box)<
  BoxProps & { type: UserRetailerCheckoutOrderStatusV2 }
>(({ theme, type }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  backgroundColor:
    type === UserRetailerCheckoutOrderStatusV2.checkout_success
      ? theme.palette.primary.main
      : type === UserRetailerCheckoutOrderStatusV2.checkout_cancelled
      ? hexadecimal("#e8e8ed")(50)
      : hexadecimal("#ff534a")(30),

  color:
    type === UserRetailerCheckoutOrderStatusV2.checkout_success
      ? theme.palette.primary.contrastText
      : type === UserRetailerCheckoutOrderStatusV2.checkout_cancelled
      ? theme.palette.primary.main
      : "#ff534a",

  padding: "8px 16px",

  borderRadius: "999px",
  fontSize: "14px",

  fontWeight: 600,
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    padding: "4px 8px",
    fontSize: "10px",
    gap: "4px",
  },
}));

const SuccessBadgeIcon = () => {
  return (
    <SvgContainer viewBox="0 0 12 12">
      <g data-name="Group 21887">
        <path
          data-name="Path 15781"
          d="M6 0a6 6 0 1 1-6 6 6 6 0 0 1 6-6z"
          style={{ fill: "#fff" }}
        />
        <path
          data-name="Path 15221"
          d="M8.77 4.022a.563.563 0 0 1 0 .744l-2.981 3.3a.425.425 0 0 1-.643.013l-1.578-1.6a.574.574 0 0 1-.035-.731.43.43 0 0 1 .655-.026L5.439 7l2.665-2.974a.433.433 0 0 1 .666-.004z"
        />
      </g>
    </SvgContainer>
  );
};

const FailedBadgeIcon = () => {
  return (
    <SvgContainer viewBox="0 0 12 12">
      <g data-name="Group 21887">
        <path
          data-name="Path 15781"
          d="M6 0a6 6 0 1 1-6 6 6 6 0 0 1 6-6z"
          style={{ fill: "#ff534a" }}
        />
        <g data-name="cancel/close">
          <g data-name="Group 418">
            <path
              data-name="Path 435"
              d="M2.362 2.116 4.175.3a.176.176 0 1 0-.244-.25L2.113 1.867.3.054A.176.176 0 0 0 .051.3l1.813 1.816L.051 3.929a.176.176 0 0 0 .249.249l1.813-1.813 1.813 1.813a.176.176 0 0 0 .249-.249z"
              transform="translate(3.884 3.885)"
              style={{ fill: "#fff", stroke: "#fff", strokeWidth: ".6px" }}
            />
          </g>
        </g>
      </g>
    </SvgContainer>
  );
};

const CancelledBadgeIcon = () => {
  return (
    <SvgContainer viewBox="0 0 12 12">
      <g data-name="Group 21955">
        <path data-name="Path 15781" d="M6 0a6 6 0 1 1-6 6 6 6 0 0 1 6-6z" />
        <g data-name="cancel/close">
          <g data-name="Group 418">
            <path
              data-name="Path 435"
              d="M2.362 2.116 4.175.3a.176.176 0 1 0-.244-.25L2.113 1.867.3.054A.176.176 0 0 0 .051.3l1.813 1.816L.051 3.929a.176.176 0 0 0 .249.249l1.813-1.813 1.813 1.813a.176.176 0 0 0 .249-.249z"
              transform="translate(3.885 3.885)"
              style={{ fill: "#fff", stroke: "#fff", strokeWidth: ".6px" }}
            />
          </g>
        </g>
      </g>
    </SvgContainer>
  );
};

// const DeliveringBadgeIcon = () => {
//   return (
//     <SvgContainer viewBox="0 0 12 12">
//       <g data-name="Group 21939">
//         <path
//           data-name="Path 25769"
//           d="M6 12a6 6 0 1 0-6-6 6.007 6.007 0 0 0 6 6zM6 .8A5.2 5.2 0 1 1 .8 6 5.2 5.2 0 0 1 6 .8z"
//           style={{ stroke: "#000", strokeWidth: ".2px" }}
//           transform="translate(.1 .1)"
//         />
//         <path
//           data-name="Path 25770"
//           d="M169.548 76.681a.345.345 0 0 0 .219.077.35.35 0 0 0 .219-.623l-1.619-1.295v-2.632a.35.35 0 1 0-.7 0v2.8a.351.351 0 0 0 .131.273z"
//           transform="translate(-161.754 -69.095)"
//           style={{ stroke: "#000", strokeWidth: ".2px" }}
//         />
//       </g>
//     </SvgContainer>
//   );
// };

const BadgeIcon = ({ type }: { type: UserRetailerCheckoutOrderStatusV2 }) => {
  switch (type) {
    case UserRetailerCheckoutOrderStatusV2.checkout_success:
      return <SuccessBadgeIcon />;
    case UserRetailerCheckoutOrderStatusV2.checkout_cancelled:
      return <CancelledBadgeIcon />;
    case UserRetailerCheckoutOrderStatusV2.checkout_error:
      return <FailedBadgeIcon />;

    default:
      return null;
  }
};

const getBadgeText = (type: UserRetailerCheckoutOrderStatusV2) => {
  switch (type) {
    case UserRetailerCheckoutOrderStatusV2.checkout_success:
      return "Delivered";
    case UserRetailerCheckoutOrderStatusV2.checkout_cancelled:
      return "Cancelled";
    case UserRetailerCheckoutOrderStatusV2.checkout_error:
      return "Failed";
    // case UserRetailerCheckoutOrderStatusV2:
    //   return "On the way";
    default:
      return "";
  }
};

const StateBadge = ({ type }: { type: UserRetailerCheckoutOrderStatusV2 }) => {
  const badgeText = getBadgeText(type);
  if (!badgeText) return null;
  return (
    <BadgeContainer type={type}>
      <BadgeIcon type={type} />
      {badgeText}
    </BadgeContainer>
  );
};

export default StateBadge;
